import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { format, isToday } from 'date-fns';
import { Button } from "app/components/ui/button";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "app/components/ui/popover";
import { useReferralListSlice } from 'app/pages/referral-list/slice';
import { useMarkAllNotificationsAsReadMutation } from 'app/slice';
import { NotificationsProps } from 'types/Notification';
import { useToast } from '../ui/use-toast';

export const Notifications = ({ 
  notifications = [], 
  onNotificationSelect,
  onNotificationRead,
  pagination,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false
}: NotificationsProps) => {
  const [isFirstSeen, setIsFirstSeen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [readNotificationIds, setReadNotificationIds] = useState<Set<string>>(new Set());
  const [selectedNotificationId, setSelectedNotificationId] = useState<string | null>(null);
  const { useMarkNotificationAsReadMutation } = useReferralListSlice();
  const [markAsRead] = useMarkNotificationAsReadMutation();
  const [markAllAsRead] = useMarkAllNotificationsAsReadMutation();
  const { toast } = useToast();

  // Initialize read notification IDs from props
  useEffect(() => {
    const readIds = new Set(
      notifications
        .filter(notification => notification.isRead === true)
        .map(notification => notification._id)
    );
    setReadNotificationIds(readIds);
  }, [notifications]);

  // Memoize computed values with local state
  const unreadCount = useMemo(() => 
    notifications.filter(notification => 
      !notification.isRead && !readNotificationIds.has(notification._id)
    ).length, 
    [notifications, readNotificationIds]
  );


  const unreadNotificationIds = useMemo(() => 
    notifications
      .filter(notification => 
        !notification.isRead && !readNotificationIds.has(notification._id)
      )
      .map(notification => notification._id),
    [notifications, readNotificationIds]
  );

  const formatNotificationDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    return isToday(date) ? 'Today' : format(date, 'MM/dd/yyyy');
  }, []);

  // Handle infinite scroll for notifications
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const threshold = 50; // pixels from bottom
    
    if (scrollHeight - scrollTop - clientHeight < threshold && hasMore && !isLoadingMore && onLoadMore) {
      onLoadMore();
    }
  }, [hasMore, isLoadingMore, onLoadMore]);

  const handleMarkAllAsRead = useCallback(() => {
    if (unreadNotificationIds.length === 0) {
      // No unread notifications, but still refetch to show updated data
      if (onNotificationRead) {
        onNotificationRead();
      }
      return;
    }

    markAllAsRead({ notificationIds: unreadNotificationIds }).unwrap()
      .then((response) => {
        const isSuccess = response && (
          response.status === true || 
          response.status === 200 || 
          response.success === true
        );

        if (isSuccess) {
          // Update local state immediately
          setReadNotificationIds(prev => {
            const newSet = new Set(prev);
            unreadNotificationIds.forEach(id => newSet.add(id));
            return newSet;
          });
          
          // After successful mark as read, refetch notifications to get updated data
          if (onNotificationRead) {
            onNotificationRead();
          }
          
          toast({
            title: "All notifications marked as read",
            description: "You have successfully marked all notifications as read.",
            variant: "default",
          });
        } else {
          toast({
            title: "Failed to mark all notifications as read",
            description: response?.data?.message || response?.message || "There was an error marking all notifications as read.",
            variant: "destructive",
          });
        }
      })
      .catch((error) => {
        toast({
          title: "Failed to mark all notifications as read",
          description: error?.data?.message || error?.message || "There was an error marking all notifications as read.",
          variant: "destructive",
        });
      });
  }, [markAllAsRead, unreadNotificationIds, toast, onNotificationRead]);

  const handleNotificationClick = useCallback((notificationId: string) => {
    const notificationData = notifications.find(notification => notification._id === notificationId);
    if (notificationData) {
    
      const entityId = notificationData?.entityId;
      setSelectedNotificationId(entityId);
      
      // Call the parent callback with the selected entity ID
      if (onNotificationSelect) {
        onNotificationSelect(entityId);
      }
    }

    markAsRead({ notificationId }).unwrap()
      .then(() => {
        // Update local state immediately
        setReadNotificationIds(prev => new Set(Array.from(prev).concat(notificationId)));
        
        // Call the parent callback to refetch notifications
        if (onNotificationRead) {
          onNotificationRead();
        }
      })
      .catch((error) => {
        toast({
          title: "Failed to mark notification as read",
          description: error?.data?.message || error?.message || "There was an error marking the notification as read.",
          variant: "destructive",
        });
      });
  }, [markAsRead, toast, notifications, onNotificationSelect, onNotificationRead]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    
    if (open) {
      // Only mark all as read if there are unread notifications
      if (unreadNotificationIds.length > 0) {
        handleMarkAllAsRead();
      }
      setTimeout(() => setIsFirstSeen(true), 1000);
    } else {
      setTimeout(() => setIsFirstSeen(false), 200);
      // Reset selected notification when popover closes
      setSelectedNotificationId(null);
      if (onNotificationSelect) {
        onNotificationSelect(null);
      }
    }
  }, [handleMarkAllAsRead, onNotificationSelect, unreadNotificationIds]);

  const handleClose = useCallback(() => setIsOpen(false), []);

  const showNotificationBadge = !isFirstSeen && unreadCount > 0;
  const buttonClassName = `relative p-3 rounded-full size-10 focus:outline-none ${
    showNotificationBadge 
      ? 'bg-[#5B73F1] hover:bg-[#4360EF] focus:ring-2 focus:ring-blue-200' 
      : 'bg-white hover:bg-[#FAFAFA]'
  }`;

  const iconClassName = showNotificationBadge ? 'filter brightness-0 invert' : '';

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className={buttonClassName}
          aria-label="Notifications"
          tabIndex={0}
        >
          {showNotificationBadge && (
            <span className="absolute top-0 right-0 flex-shrink-0 flex items-center justify-center bg-[#B3171A] min-w-3.5 min-h-3.5 px-[2px] py-px border-[1.3px] border-white rounded-full font-inter text-center text-white text-[0.5rem] font-bold leading-[0.5rem] tracking-[-0.015rem]">
              {unreadCount}
            </span>
          )}
          <img
            src="/images/notification.svg"
            alt="Notifications"
            className={iconClassName}
          />
        </Button>
      </PopoverTrigger>
      
      {notifications.length > 0 && (
        <PopoverContent
          className="w-[20.1875rem] bg-white p-0 border-white rounded-lg shadow-[0px_1px_4px_0px_rgba(0,0,0,0.25)] overflow-hidden"
          align="end"
          sideOffset={4}
        >
          <div className="flex justify-between items-start px-5 py-4">
            <h3 className="font-serif text-[#121619] text-sm font-bold leading-normal">
              Notifications
            </h3>
            <Button
              variant="ghost"
              onClick={handleClose}
              className="p-0 opacity-80 transition-opacity duration-200 hover:bg-transparent focus:outline-none hover:opacity-100"
            >
              <img src="/images/close.svg" alt="Close" className="size-2.5" />
            </Button>
          </div>
          
          <div 
            className="custom-scrollbar max-h-[25rem] space-y-2 overflow-y-auto"
            onScroll={handleScroll}
          >
            {notifications.map((notification) => {
              const isHighlighted = notification._id === '1' && !isFirstSeen;
              const isRead = notification.isRead === true || readNotificationIds.has(notification._id);
              const notificationClassName = `flex gap-2.5 px-5 py-2 transition-colors cursor-pointer ${
                isHighlighted 
                  ? 'bg-[#F2F4F8] hover:bg-[#EEF1F6]' 
                  : 'bg-white hover:bg-[#F1F1F1]'
              }`;

              const iconBgColor = notification.type === 'referral' ? 'bg-[#FF85BC]' : 'bg-[#55B494]';
              const iconSrc = notification.type === 'referral' ? '/images/refer.svg' : '/images/calendar-plus.svg';
              const envelopeSrc = !isRead 
                ? '/images/envelope-close.svg' 
                : '/images/envelope-open.svg';
              const envelopeAlt = !isRead ? 'Envelope Close' : 'Envelope Open';

              return (
                <div
                  key={notification._id}
                  onClick={() => handleNotificationClick(notification._id)}
                  className={notificationClassName}
                >
                  <div className={`flex items-center justify-center size-5 rounded-full flex-shrink-0 ${iconBgColor}`}>
                    <img
                      src={iconSrc}
                      alt={notification.type}
                      className="size-2.5 object-contain"
                    />
                  </div>
                  <h4 className="max-w-[11.6875rem] text-[#21272A] text-xs font-normal leading-[0.975rem]">
                  You have received a {" "}
                    <span className="font-semibold capitalize">
                    New {notification.type === 'referral' ? 'Referral' : 'Appointment Request'}
                    </span>{" "}
                    from{" "}
                    <span className="font-semibold capitalize">
                      {notification.entityName}
                    </span>
                  </h4>
                  <div className="flex flex-col items-end gap-1.5 ml-auto">
                    <span className="text-[#878D96] text-[0.625rem] font-normal leading-normal">
                      {formatNotificationDate(notification.createdAt)}
                    </span>
                    <img 
                      src={envelopeSrc} 
                      alt={envelopeAlt} 
                      className="object-contain size-3" 
                    />
                  </div>
                </div>
              );
            })}
            
            {/* Loading indicator for pagination */}
            {isLoadingMore && (
              <div className="flex justify-center items-center py-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#5B73F1]"></div>
                <span className="ml-2 text-[#878D96] text-xs">Loading more...</span>
              </div>
            )}
            
            {/* End of notifications indicator */}
            {!hasMore && notifications.length > 0 && (
              <div className="flex justify-center items-center py-2">
                <span className="text-[#878D96] text-xs">No more notifications</span>
              </div>
            )}
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
};
